import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import messages from "../../../../../../assets/locale/messages";
import CompanyActivityLogView from "../../../../../../components/ActivityLog/CompanyActivityLogView";
import {
  COMPANY_ACTIVITY_MODULE_BY_API_KEY,
  toActivityLogRow,
} from "../../../../../../components/Dashboard/RecentActivityLog/constants";
import { getActivityLogs } from "../../../../../../network/apis/activityLogs";
import { getAdminCompanyRoles } from "../../../../../../network/apis/adminCompanyRoles";
import { extractAdminPortalList } from "../../../../../../utils/adminPortalHelpers";
import { normalizeCompanyRoles } from "../../../../../Company-portal/TeamsPermissions/roleMappers";

/** Role filter value for the company owner (PROLINE-3925) — not a company role. */
const COMPANY_OWNER_ROLE_ID = "company_owner";

const toRow = (entry) =>
  toActivityLogRow(entry, COMPANY_ACTIVITY_MODULE_BY_API_KEY);

const EMPTY_PAGE = { list: [], count: 0, pages: 1 };

/**
 * The view's PROLINE-3925 params → the Super Admin API's (PROLINE-3943): this
 * company's user entries only, and the search goes as `query`.
 * `company_role_id` passes through as-is (PROLINE-3988).
 */
const toAdminParams = ({ company_user_name: query, ...params }, companyId) => ({
  ...params,
  ...(query ? { query } : {}),
  actor_type: "CompanyUser",
  company_id: companyId,
});

/**
 * Company profile "Logs" tab (Super Admin) — the same screen the company sees
 * in its own portal, for this company's entries only
 * (GET /admin_portal/v2/activity_logs?actor_type=CompanyUser&company_id=…).
 * Filters: module, date range and role (this company's roles + Company Owner).
 * Keeps its entries in local state, not the shared `activityLogs` store, so the
 * Super Admin's own Activity Log (or another company's tab) never shows here.
 */
const CompanyActivityLogTab = ({ companyId }) => {
  const lang = useSelector((state) => state.locale.lang);
  const companyOwnerLabel = messages[lang].activityLog.company.filters.companyOwner;
  const [companyRoles, setCompanyRoles] = useState([]);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  // Latest wins — a slower earlier request must not overwrite newer results.
  const latestRequest = useRef(0);

  const onQueryChange = useCallback(
    async (params) => {
      const requestId = ++latestRequest.current;
      setLoading(true);
      try {
        const response = await getActivityLogs(toAdminParams(params, companyId));
        if (requestId !== latestRequest.current) return;
        const data = response.data?.data;
        setResult({
          list: Array.isArray(data) ? data : [],
          count: +response.headers["total-count"] || 0,
          pages: +response.headers["total-pages"] || 1,
        });
      } catch {
        // The request interceptor already shows the error toast.
        if (requestId === latestRequest.current) setResult(EMPTY_PAGE);
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    let cancelled = false;
    getAdminCompanyRoles({ company_id: companyId, page: -1 })
      .then((response) => {
        if (!cancelled) {
          setCompanyRoles(normalizeCompanyRoles(extractAdminPortalList(response)));
        }
      })
      // The request interceptor already shows the error toast.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // This company's roles + a static "Company Owner" option.
  const roleOptions = useMemo(
    () => [
      { id: COMPANY_OWNER_ROLE_ID, name: companyOwnerLabel },
      ...companyRoles.map(({ id, name }) => ({ id, name })),
    ],
    [companyRoles, companyOwnerLabel],
  );

  const rows = useMemo(() => result.list.map(toRow), [result.list]);

  /** Export: every entry matching the current filters (`page=-1`). */
  const loadExportEntries = async (filterParams) => {
    try {
      const response = await getActivityLogs({
        ...toAdminParams(filterParams, companyId),
        page: -1,
      });
      return (response.data?.data || []).map(toRow);
    } catch {
      // The request interceptor already shows the error toast.
      return null;
    }
  };

  return (
    <CompanyActivityLogView
      rows={rows}
      count={result.count}
      pages={result.pages}
      loading={loading}
      roleOptions={roleOptions}
      onQueryChange={onQueryChange}
      loadExportEntries={loadExportEntries}
    />
  );
};

CompanyActivityLogTab.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default CompanyActivityLogTab;
